import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, SlidersHorizontal, RotateCcw } from "lucide-react";
import { RATING } from "@/lib/capitalLabels";
import { toast } from "sonner";

export default function PortalRiskProfile({ clientId }) {
  const [data, setData] = useState(null);

  const qp = clientId ? `?client_id=${clientId}` : "";
  const load = async () => {
    try { setData((await api.get(`/client-portal/risk-profile${qp}`)).data); }
    catch (e) { toast.error(e.response?.data?.detail || "Risk profile unavailable"); }
  };
  useEffect(() => { load(); }, [clientId]); // eslint-disable-line

  const setRating = async (asset_type, rating) => {
    try {
      await api.put(`/client-portal/risk-profile${qp}`, { asset_type, rating });
      toast.success(rating ? `${asset_type} → consequence rating ${rating}` : `${asset_type} reset to GBE default`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const resetAll = async () => {
    if (!confirm("Reset every asset type back to GBE default ratings?")) return;
    try {
      const { data: r } = await api.post(`/client-portal/risk-profile/reset${qp}`);
      toast.success(`Cleared ${r.cleared} custom rating(s)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); }
  };

  if (!data) {
    return <div className="flex items-center gap-2 text-sm text-slate-500 py-10 justify-center">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading risk profile…</div>;
  }

  return (
    <div className="space-y-4" data-testid="portal-risk-profile">
      <div className="flex items-start gap-2">
        <SlidersHorizontal className="w-4 h-4 text-gbe mt-0.5" />
        <div className="flex-1">
          <div className="text-sm font-semibold text-slate-800">Consequence risk profile</div>
          <div className="text-[11px] text-slate-500">
            Set how severe a failure of each asset type is for <span className="font-medium">your sites</span> (1 = negligible → 5 = whole-site outage).
            Your ratings adjust the Impact shown across your portfolio; GBE defaults apply where you haven't set one.
          </div>
        </div>
        {data.customised > 0 && <Badge variant="outline" className="text-[10px] bg-sky-50 text-sky-700 border-sky-200">{data.customised} customised</Badge>}
        <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={resetAll} data-testid="rp-reset-all">
          <RotateCcw className="w-3 h-3 mr-1" /> Reset all</Button>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase text-slate-500">
            <tr>
              <th className="text-left px-4 py-2">Asset type</th>
              <th className="text-right px-3 py-2">Assets</th>
              <th className="text-left px-3 py-2">GBE default</th>
              <th className="text-left px-3 py-2">Your rating (1–5)</th>
              <th className="text-left px-3 py-2">Effective impact</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.asset_type} className="border-b border-slate-100 hover:bg-slate-50/40" data-testid={`rp-row-${r.asset_type}`}>
                <td className="px-4 py-2 font-medium text-slate-800">{r.asset_type}
                  {r.asset_group && <span className="text-slate-400 font-normal ml-1.5 text-[10px]">{r.asset_group}</span>}</td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-500">{r.count}</td>
                <td className="px-3 py-2 text-slate-500">{RATING[r.default_rating]?.short}</td>
                <td className="px-3 py-2">
                  <Select value={r.custom_rating ? String(r.custom_rating) : "default"}
                          onValueChange={(v) => setRating(r.asset_type, v === "default" ? null : Number(v))}>
                    <SelectTrigger className={`h-7 w-44 text-[11px] ${r.custom_rating ? "border-sky-300 bg-sky-50/50" : ""}`}
                                   data-testid={`rp-select-${r.asset_type}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">GBE default</SelectItem>
                      {[5, 4, 3, 2, 1].map((n) => <SelectItem key={n} value={String(n)}>{n} — {RATING[n].label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-3 py-2">
                  <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] font-medium ${RATING[r.effective_rating]?.cls}`}>
                    {RATING[r.effective_rating]?.short}
                  </span>
                  {r.custom_rating && <span className="ml-1.5 text-[9px] text-sky-600">client-tuned</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.audit?.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Change history</div>
          <div className="space-y-1 max-h-40 overflow-y-auto" data-testid="rp-audit">
            {data.audit.map((a, i) => (
              <div key={i} className="text-[11px] text-slate-500">
                <span className="text-slate-400">{(a.at || "").slice(0, 16).replace("T", " ")}</span>{" "}
                <span className="font-medium text-slate-700">{a.asset_type === "*" ? "All types" : a.asset_type}</span>{" "}
                {a.to ? `→ rating ${a.to}` : "→ GBE default"}
                {a.from != null && <span className="text-slate-400"> (was {a.from})</span>}
                <span className="text-slate-400"> by {a.by}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
